import React, { useContext, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getApolloContext } from "@apollo/client";
import { Bounce, ToastContainer } from "react-toastify";
import { useIdleTimer } from "react-idle-timer";
import queryString from "query-string";
import { get, isEmpty, isNil } from "lodash";
import { useAuthApollo } from "utils/AuthApolloInitializer";
import { useAuth0 } from "utils/react-auth0-wrapper";
import {
  getUserState,
  linkPaychexPerksUser,
  linkPaychexRolloverUser,
  setInitialUserLoading,
} from "actions/userActions";
import {
  getLinkedCompanies,
  setEmployerSession,
} from "actions/employerActions";
import { adminSite, employerSite, individualSite } from "utils/determineSite";
import { useMediaQuery } from "react-responsive";
import { safeInternalPath, setNavigate } from "utils/navigate";

import AccessTokenStorage from "utils/AccessTokenStorage";
import Loading from "components/Loading";
import IndividualRoutes from "routes/IndividualRoutes";
import AdminRoutes from "routes/AdminRoutes";
import EmployerRoutes from "routes/EmployerRoutes";
import IconLayout from "./components/IconLayout";
import NavigationScrollReset from "./components/NavigationScrollReset";

import "./Employer.scss";
import "./Individual.scss";
import "./Admin.scss";

import "./App.scss";
import {
  getPath,
  iconIdSelector,
  needsPaychexPerksLinkSelector,
  needsPaychexRolloverLinkSelector,
} from "store/selectors/auth0";
import { setAccountSession } from "actions/accountActions";
import type { AppDispatch } from "store/configureStore";

const App = () => {
  const { user, isAuthenticated, logout } = useAuth0();
  const { tokenLoading } = useAuthApollo()!;
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const apolloContext = useContext(getApolloContext());
  const client = apolloContext?.client || null;
  const isSmallScreen = useMediaQuery({ maxWidth: 767 });
  // The claim stays true for the life of the token, so bound the link to one
  // in-flight call per page load.
  const perksLinkAttempted = useRef(false);
  const rolloverLinkAttempted = useRef(false);

  // Register navigate for use outside of React components (thunks, Apollo error handlers)
  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);

  // Restore the pre-auth URL after Auth0 returns. We save it to sessionStorage
  // in PrivateRoute before loginWithRedirect; relying on Auth0's
  // onRedirectCallback + navigate() races against the router/auth state
  // settling, sometimes landing users on /login (which then routes to /dashboard).
  //
  // Don't clear sessionStorage until we've actually landed on the target URL.
  // PublicRoute checks for this key to skip its <Navigate to="/login"> guard —
  // clearing it too early lets PublicRoute race our navigate() and steal the route.
  useEffect(() => {
    if (!isAuthenticated) return;
    const stored = sessionStorage.getItem("auth0_postLoginRedirect");
    if (!stored) return;
    // Read from window.location, not React Router's location. Auth0's
    // onRedirectCallback strips ?code=…&state=… via window.history.replaceState
    // (which RR doesn't observe), so on the first post-login render
    // location.search is stale (still has code/state) while window.location.search
    // already matches `stored`. Using the stale RR value made current !== stored
    // when it should have, so we'd call navigate(stored, replace) and that
    // navigate ran after PublicRoute's <Navigate to="/login/?…"> in the same
    // effect batch — clobbering it. PublicRoute would re-render with the same
    // `to`, but <Navigate>'s effect deps are stable so it wouldn't re-fire,
    // leaving the user stuck at `/`.
    const current = `${window.location.pathname}${window.location.search}`;
    // Always clear stored here. Either we're already at the target (nothing
    // to do) or we're about to navigate there. We do NOT wait for the URL to
    // confirm — once we've issued the navigate, downstream redirects (e.g.
    // PublicRoute taking the user to /login/?…) are legitimate and we must
    // not pull them back to `stored`. If we left stored in place, this effect
    // would re-fire on each location change and keep yanking the user back
    // to the entry URL.
    sessionStorage.removeItem("auth0_postLoginRedirect");
    if (current !== stored) {
      void navigate(stored, { replace: true });
    }
  }, [isAuthenticated, navigate, location.pathname, location.search]);

  const thirtyMinutes = 30 * 1000 * 60;
  // eslint-disable-next-line typescript/unbound-method -- react-idle-timer returns stable, pre-bound methods; no `this` capture
  const { reset, start } = useIdleTimer({
    timeout: thirtyMinutes,
    onIdle: () => {
      // stop the idle timer for the login page -- it should only be started when the user is authenticated
      reset();
      logout({
        logoutParams: {
          returnTo: `${window.location.origin}/login?redirect_to=${window.location.pathname}${window.location.search}`,
        },
      });
    },
    startOnMount: false,
    startManually: true,
  });

  // Idle timer starts unmounted to account for the login page. Once authenticated, begin the timer
  useEffect(() => {
    if (!tokenLoading && AccessTokenStorage.getToken()) {
      start();
    }
  }, [tokenLoading, start]);

  // Setup initial state, and getState if logged in
  useEffect(() => {
    if (tokenLoading || !isAuthenticated || !client) return;

    const setupState = async () => {
      dispatch(setInitialUserLoading(true));
      try {
        // ensure that we call getState to set the app up properly in case earlier async
        // calls to it (e.g. from /login redirects) failed
        const userHasIconAccount = !isEmpty(iconIdSelector(user));

        if (employerSite()) {
          const response = await dispatch(getUserState(client, user!));
          // boundary: dispatch result is a success|failure union; .data exists only on success
          const companyId = get(
            (response as { data?: unknown }).data,
            "company.id"
          );
          // This will execute during employer registration and cause a false positive error unless we check for email verification
          if (user!.email_verified && !isNil(companyId)) {
            void dispatch(getLinkedCompanies(client));
          }
          const targetCompanyId = get(
            queryString.parse(window.location.search),
            "targetCompany"
          );
          const companyIdsDoNotMatch =
            !isEmpty(targetCompanyId) && companyId !== targetCompanyId;
          if (companyIdsDoNotMatch) {
            const employerSessionResponse = await dispatch(
              // boundary: query-string value is widely typed; runtime guard above ensures a string
              setEmployerSession(client, targetCompanyId as string)
            );
            // only reload page if the session change actually worked
            if (
              isEmpty((employerSessionResponse as { error?: unknown }).error)
            ) {
              window.location.reload();
            } else {
              void navigate(safeInternalPath(window.location.pathname), {
                replace: true,
              });
            }
          }
        }

        if (individualSite() && userHasIconAccount) {
          if (
            needsPaychexPerksLinkSelector(user) &&
            !perksLinkAttempted.current
          ) {
            perksLinkAttempted.current = true;
            const linkResponse = await dispatch(linkPaychexPerksUser(client));
            // The Perks claims are minted at login, so the current token still
            // reads as non-Perks. Reload for a token that reflects the link —
            // same reason as the setAccountSession branch above.
            if (isEmpty((linkResponse as { error?: unknown }).error)) {
              window.location.reload();
              return;
            }
          }

          // Has to run on this login: the Paychex access token the link needs was
          // re-minted by it and nothing else refreshes it.
          if (
            needsPaychexRolloverLinkSelector(user) &&
            !rolloverLinkAttempted.current
          ) {
            rolloverLinkAttempted.current = true;
            const rolloverLinkResponse = await dispatch(
              linkPaychexRolloverUser(client)
            );
            // Gate on the payload, not just the absence of an error: a stale
            // Paychex token resolves false rather than throwing and leaves
            // app_metadata alone, so the Action re-mints the claim on the
            // reload's silent auth and we would reload forever.
            if (
              (rolloverLinkResponse as { data?: unknown }).data === true &&
              isEmpty((rolloverLinkResponse as { error?: unknown }).error)
            ) {
              window.location.reload();
              return;
            }
          }
          await dispatch(getUserState(client, user));
          const activeAccountIdPath = `${getPath()}/active_account_id`;

          const accountId = get(user, activeAccountIdPath);
          const targetAccountId = get(
            queryString.parse(window.location.search),
            "account_id"
          );

          if (!isEmpty(targetAccountId)) {
            const accountIdsDoNotMatch = accountId !== targetAccountId;
            if (accountIdsDoNotMatch) {
              const accountSessionResponse = await dispatch(
                // boundary: query-string value is widely typed; runtime guard above ensures a string
                setAccountSession(client, targetAccountId as string)
              );
              if (
                isEmpty((accountSessionResponse as { error?: unknown }).error)
              ) {
                window.location.reload();
              } else {
                void navigate(safeInternalPath(window.location.pathname), {
                  replace: true,
                });
              }
            } else {
              // remove from url
              window.history.pushState({}, "", window.location.pathname);
            }
          }
        }

        if (adminSite()) {
          await dispatch(getUserState(client, user));
        }
      } catch (e) {
        console.log(e);
      } finally {
        dispatch(setInitialUserLoading(false));
      }
    };

    void setupState();
  }, [tokenLoading, isAuthenticated, client]);

  // Handle redirect_to query param
  useEffect(() => {
    const queryObj = queryString.parse(window.location.search);
    if (queryObj.redirect_to) {
      void navigate(safeInternalPath(queryObj.redirect_to), { replace: true });
    }
  }, []);

  if (tokenLoading) {
    console.log("Loading...");
    return <Loading />;
  }

  console.log("Providers Loaded.");

  let activeRoutes: ReactNode = null;

  if (employerSite()) {
    activeRoutes = <EmployerRoutes />;
  } else if (individualSite()) {
    activeRoutes = <IndividualRoutes />;
  } else if (adminSite()) {
    activeRoutes = <AdminRoutes isAuthenticated={isAuthenticated} />;
  }

  return (
    <>
      <NavigationScrollReset />
      <IconLayout routes={activeRoutes} isSmallScreen={isSmallScreen} />
      <ToastContainer position="bottom-right" transition={Bounce} />
    </>
  );
};

export default App;
